import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, apiRequest, loginRequest } from '../authConfig';

const POPUP_TIMEOUT_MS = 30_000;

/**
 * Race a promise against a timeout so a popup blocker or user-not-responding
 * can't hang an API call indefinitely (Perf-2). Aborting is best-effort —
 * MSAL's popup is a detached window so we can't truly cancel it, but
 * rejecting the outer promise unblocks the fetch caller.
 */
function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms — popup blocked, or user did not respond`));
    }, ms);
    p.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

/**
 * Acquire an access token for the DevGPT backend API.
 * Silent-first; falls back to interactive popup when silent acquisition fails
 * (e.g. consent required, refresh token expired, MFA step-up).
 */
async function getApiToken(): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new Error('No signed-in account; user must sign in before calling the API');
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiRequest, account });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Perf-2: cap the popup wait. A 30s ceiling is generous for the user
      // to click "Approve" but short enough that a blocked popup surfaces
      // as a usable error rather than a spinner that never resolves.
      const result = await withTimeout(
        msalInstance.acquireTokenPopup({ ...apiRequest, account }),
        POPUP_TIMEOUT_MS,
        'acquireTokenPopup',
      );
      return result.accessToken;
    }
    throw err;
  }
}

/**
 * fetch() wrapper that attaches an Entra ID access token to the
 * Authorization header. All calls into the DevGPT backend should use this
 * instead of bare fetch.
 *
 * H7 — 401 interception: if the server rejects our token (e.g. expired
 * mid-session, or revoked), we try one silent re-acquire and retry the
 * request once. If that still 401s, we redirect to login instead of
 * showing the user a cryptic error.
 */
export async function fetchWithAuth(url: string, init: RequestInit = {}): Promise<Response> {
  const token = await getApiToken();
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  const response = await fetch(url, { ...init, headers });

  if (response.status !== 401) return response;

  // Try one silent refresh + retry. If the backend rejects again, our
  // stored token is unrecoverable (revoked, user signed out elsewhere,
  // tenant policy change). Kick the user to the login flow.
  try {
    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (!account) throw new Error('no account to refresh');
    // Force a silent re-acquisition by setting forceRefresh.
    const refreshed = await msalInstance.acquireTokenSilent({
      ...apiRequest,
      account,
      forceRefresh: true,
    });
    const retryHeaders = new Headers(init.headers);
    retryHeaders.set('Authorization', `Bearer ${refreshed.accessToken}`);
    const retryResponse = await fetch(url, { ...init, headers: retryHeaders });
    if (retryResponse.status !== 401) return retryResponse;
  } catch {
    // Fall through to login redirect.
  }

  // Persistent 401 after refresh → session is dead. Send the user back
  // to MSAL login. loginRedirect navigates the whole window.
  await msalInstance.loginRedirect(loginRequest);
  // loginRedirect never resolves, but TypeScript needs a return.
  return response;
}
